import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/api";
import PasswordInput from "../PasswordInput";

const Register = () => {
  const [ownerName, setOwnerName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [gstin, setGstin] = useState("");

  const [statesList, setStatesList] = useState([]);
  const [districtsList, setDistrictsList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // 🔥 Load States
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/location/states`);
        const data = await res.json();
        if (res.ok) {
          setStatesList(data);
        }
      } catch (err) {
        console.error("Failed to load states");
      }
    };

    fetchStates();
  }, []);

  // 🔥 Load Districts when state changes
  useEffect(() => {
    if (!state) return;

    const fetchDistricts = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/location/districts/${state}`
        );
        const data = await res.json();
        if (res.ok) {
          setDistrictsList(data);
        }
      } catch (err) {
        console.error("Failed to load districts");
      }
    };

    fetchDistricts();
  }, [state]);

  const handleRegister = async () => {
    setError("");
    if (!agreedToTerms) {
      setError("Please agree to the Terms & Conditions to register.");
      return;
    }
    if (gstin.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstin.trim().toUpperCase())) {
      setError("Invalid GST Number format. It should be 15 characters like: 37AABCS1234A1ZX");
      return;
    }
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ownerName,
          companyName,
          phone,
          city,
          state,
          district,
          email,
          password,
          gstin: gstin.trim().toUpperCase(),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate("/"), 3000);
      } else {
        setError(data.message || "Registration failed. Please try again.");
      }
    } catch {
      setError("Could not connect to server. Please try again.");
    }

    setLoading(false);
  };

  // ── Success screen ──
  if (success) {
    return (
      <div className="auth-form" style={{ textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
        <h2 style={{ color: "#15803d", marginBottom: 8 }}>You're registered!</h2>
        <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, marginBottom: 20 }}>
          Your free trial account is ready.<br/>
          You can start reporting frauds right away.<br/>
          <span style={{ color: "#6b7280", fontSize: 13 }}>Redirecting to login…</span>
        </p>
        <div style={{ background: "#f0fdf4", borderRadius: 12, padding: "14px 18px", marginBottom: 20, textAlign: "left", border: "1.5px solid #bbf7d0" }}>
          <div style={{ fontSize: 13, color: "#166534", lineHeight: 2 }}>
            ✅ Report unlimited frauds — free, forever<br/>
            🔒 Upgrade anytime to unlock fraud search<br/>
            📧 Check your email for a welcome message
          </div>
        </div>
        <Link to="/" style={{ color: "#4318FF", fontWeight: 700, fontSize: 14 }}>Go to Login →</Link>
      </div>
    );
  }

  return (
    <div className="auth-form">
      <h2>Hello!</h2>
      <p className="subtitle">Sign Up to Get Started — Free Forever</p>

      {error && (
        <div style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
          ❌ {error}
        </div>
      )}

      <input
        type="text"
        placeholder="Full Name"
        value={ownerName}
        onChange={(e) => setOwnerName(e.target.value)}
      />

      <input
        type="text"
        placeholder="Company Name"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
      />

      <input
        type="text"
        placeholder="Phone Number"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
      />

      {/* ✅ STATE DROPDOWN */}
      <select
        value={state}
        onChange={(e) => {
          setState(e.target.value);
          setDistrict("");
        }}
      >
        <option value="">Select State</option>
        {statesList.map((s, index) => (
          <option key={index} value={s}>
            {s}
          </option>
        ))}
      </select>

      {/* ✅ DISTRICT DROPDOWN */}
      <select
        value={district}
        onChange={(e) => setDistrict(e.target.value)}
        disabled={!state}
      >
        <option value="">Select District</option>
        {districtsList.map((d, index) => (
          <option key={index} value={d}>
            {d}
          </option>
        ))}
      </select>

      {/* Keep city for exact area/locality */}
      <input
        type="text"
        list="registration-city-options"
        placeholder="City / Area (e.g. Rajahmundry)"
        value={city}
        onChange={(e) => setCity(e.target.value)}
      />
      <datalist id="registration-city-options">
        <option value="Rajamahendravaram" label="Rajahmundry" />
      </datalist>

      <input
        type="email"
        placeholder="Email Address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      <PasswordInput
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        wrapperStyle={{ marginBottom: 14 }}
      />

      <input
        type="text"
        placeholder="GST Number (optional)"
        value={gstin}
        onChange={(e) => setGstin(e.target.value.toUpperCase())}
        maxLength={15}
        style={{ letterSpacing: "0.5px" }}
      />
      <p style={{ fontSize: 11, color: "#9ca3af", marginTop: -10, marginBottom: 14 }}>
        If you are GST-registered, enter your GSTIN. It will appear on your tax invoice.
      </p>

      <div style={{ display:"flex", alignItems:"flex-start", gap:10, margin:"12px 0", fontSize:13, color:"#374151" }}>
        <input type="checkbox" id="terms" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} style={{ marginTop:2, width:15, height:15, cursor:"pointer" }} />
        <label htmlFor="terms" style={{ cursor:"pointer", lineHeight:1.6 }}>
          I agree to the <a href="/terms" target="_blank" style={{ color:"#4318FF", fontWeight:700 }}>Terms & Conditions</a>, <a href="/privacy" target="_blank" style={{ color:"#4318FF", fontWeight:700 }}>Privacy Policy</a> and <a href="/refund-policy" target="_blank" style={{ color:"#4318FF", fontWeight:700 }}>Refund Policy</a>
        </label>
      </div>

      <button
        className="primary-btn"
        onClick={handleRegister}
        disabled={loading}
      >
        {loading ? "Creating Account..." : "Create Free Account"}
      </button>

      <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", margin: "8px 0 14px", lineHeight: 1.5 }}>
        Free forever · No credit card needed · Start reporting frauds immediately
      </p>

      <p className="link-text">
        <Link to="/">Already have an account? Login</Link>
      </p>
    </div>
  );
};

export default Register;